import { QSetReviewStatusTag } from '@exam-management/components';
import { WorkflowStepCode } from '@exam-management/graphql';
import {
  Card,
  Flex,
  Text,
  type CardProps,
  mergeSx,
  EllipsisText,
  CalendarTodayOutlinedIcon,
  LocalOfferOutlinedIcon,
} from '@shared/components';
import { formatDateTime } from '@shared/utils';

import { type SubtaskDetails } from '../../types';

export const QSET_REVIEW_STATUS_COLOR = {
  [WorkflowStepCode.ReviewQsetTodo]: 'info.main',
  [WorkflowStepCode.ReviewQsetInReview]: 'warning.main',
  [WorkflowStepCode.ReviewQsetReviewed]: 'success.main',
  [WorkflowStepCode.ReviewQsetDone]: 'success.main',
};

export type ExamEventCardProps = Pick<CardProps, 'onClick' | 'sx'> & {
  isOwnerOrLeadReviewer: boolean;
  subtask: SubtaskDetails;
  endAdornment?: React.ReactNode;
};

export default function QSetSubtaskCard(props: Readonly<ExamEventCardProps>) {
  const { subtask, sx, onClick, endAdornment, isOwnerOrLeadReviewer } = props;

  const { assignee, currentWorkflowStep, code, createdAt, submittedAt } = subtask;

  const assigneeName = assignee ? `${assignee.firstname} ${assignee.lastname}` : '-';

  const borderColor =
    QSET_REVIEW_STATUS_COLOR[currentWorkflowStep.code as keyof typeof QSET_REVIEW_STATUS_COLOR] ??
    'grey.500';

  const isClickable =
    currentWorkflowStep.code !== WorkflowStepCode.ReviewQsetTodo || isOwnerOrLeadReviewer;

  return (
    <Card
      elevation={0}
      sx={mergeSx(
        (theme) => ({
          border: '1px solid',
          borderColor: theme.palette.grey[300],
          borderRadius: theme.shape.rounded(2),
          px: theme.spacing(4),
          py: theme.spacing(3),
          cursor: isClickable && onClick ? 'pointer' : 'default',
        }),
        sx,
      )}
      onClick={isClickable ? onClick : undefined}
    >
      <Flex sx={(theme) => ({ alignItems: 'center', gap: theme.spacing(4) })}>
        <Flex
          sx={(theme) => ({
            flex: 1,
            flexDirection: 'column',
            gap: theme.spacing(2),
            borderLeft: '4px solid',
            borderColor,
            pl: theme.spacing(3),
          })}
        >
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(2),
            })}
          >
            <EllipsisText
              variant='body1Semibold'
              sx={(theme) => ({ wordBreak: 'break-all', lineHeight: theme.spacing(5) })}
            >
              {assigneeName}
            </EllipsisText>
          </Flex>
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(3),
            })}
          >
            <Flex
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                alignItems: 'center',
                gap: theme.spacing(1),
              })}
            >
              <LocalOfferOutlinedIcon sx={{ fontSize: '16px' }} />
              <Text variant='body2'>Task ID:</Text>
              <Text variant='body2'>{code}</Text>
            </Flex>
            <Flex
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                alignItems: 'center',
                gap: theme.spacing(1),
              })}
            >
              <CalendarTodayOutlinedIcon sx={{ fontSize: '16px' }} />
              <Text variant='body2'>Assigned Date:</Text>
              <Text variant='body2'>{formatDateTime(createdAt)}</Text>
            </Flex>
            <Flex
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                alignItems: 'center',
                gap: theme.spacing(1),
              })}
            >
              <CalendarTodayOutlinedIcon sx={{ fontSize: '16px' }} />
              <Text variant='body2'>Submitted Date:</Text>
              <Text variant='body2'>{formatDateTime(submittedAt)}</Text>
            </Flex>
          </Flex>
        </Flex>
        {/* End Adornment */}
        <Flex sx={(theme) => ({ gap: theme.spacing(4), alignItems: 'center' })}>
          <QSetReviewStatusTag status={currentWorkflowStep.code} />
          {endAdornment}
        </Flex>
      </Flex>
    </Card>
  );
}
